import React from 'react';
import { Printer, Download, X, Copy, Check } from 'lucide-react';
import { MasterProfile } from '../types';

interface CvPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  masterProfile: MasterProfile;
  tailoredSummary?: string;
  tailoredBullets?: string[];
}

export const CvPreviewModal: React.FC<CvPreviewModalProps> = ({
  isOpen,
  onClose,
  masterProfile,
  tailoredSummary,
  tailoredBullets
}) => {
  const [copied, setCopied] = React.useState(false);

  if (!isOpen) return null;

  const handlePrint = () => {
    window.print();
  };

  const summaryToUse = tailoredSummary || masterProfile.summary;
  const bulletsToUse = tailoredBullets && tailoredBullets.length > 0 ? tailoredBullets : masterProfile.experiences[0]?.bullets || [];

  const handleCopyMarkdown = () => {
    const md = `
# ${masterProfile.fullName}
**${masterProfile.title}**
${masterProfile.email} | ${masterProfile.phone} | ${masterProfile.location}
LinkedIn: ${masterProfile.linkedinUrl} | GitHub: ${masterProfile.githubUrl}

## Professional Summary
${summaryToUse}

## Core Competencies & Technical Skills
${masterProfile.skills.join(' • ')}

## Professional Experience
### ${masterProfile.experiences[0]?.role} - ${masterProfile.experiences[0]?.company} (${masterProfile.experiences[0]?.period})
${bulletsToUse.map(b => `- ${b}`).join('\n')}

${masterProfile.experiences.slice(1).map(exp => `
### ${exp.role} - ${exp.company} (${exp.period})
${exp.bullets.map(b => `- ${b}`).join('\n')}
`).join('\n')}

## Education
${masterProfile.education.map(ed => `- **${ed.degree}**, ${ed.institution} (${ed.year})`).join('\n')}
`;

    navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto print:p-0 print:bg-white animate-in fade-in">
      
      {/* Container */}
      <div className="w-full max-w-4xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden print:border-none print:shadow-none print:bg-white print:max-w-none print:w-full">
        
        {/* Header Action Bar (Hidden when printing) */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950 print:hidden">
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-emerald-400" />
            <h3 className="text-sm font-bold text-white">ATS-Optimized Printable CV View</h3>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleCopyMarkdown}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'Copied Markdown' : 'Copy as Text / MD'}</span>
            </button>

            <button
              onClick={handlePrint}
              className="flex items-center space-x-1.5 px-4 py-1.5 rounded-lg text-xs font-bold bg-sky-500 hover:bg-sky-400 text-white shadow-md shadow-sky-500/25"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print to PDF (Ctrl+P)</span>
            </button>

            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* The Printable Resume Body (Rendered in clean ATS-compliant single-column layout) */}
        <div className="p-8 sm:p-12 bg-white text-slate-900 font-sans leading-normal max-h-[80vh] overflow-y-auto print:max-h-none print:overflow-visible print:p-0">
          
          {/* Header */}
          <div className="border-b-2 border-slate-900 pb-4 mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 uppercase">
              {masterProfile.fullName}
            </h1>
            <p className="text-sm font-semibold text-sky-700 mt-0.5">
              {masterProfile.title}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 mt-2">
              <span>{masterProfile.email}</span>
              <span>•</span>
              <span>{masterProfile.phone}</span>
              <span>•</span>
              <span>{masterProfile.location}</span>
              <span>•</span>
              <span>{masterProfile.linkedinUrl}</span>
              <span>•</span>
              <span>{masterProfile.githubUrl}</span>
            </div>
          </div>

          {/* Professional Summary */}
          <div className="mb-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-2">
              Professional Summary
            </h2>
            <p className="text-xs text-slate-700 leading-relaxed">
              {summaryToUse}
            </p>
          </div>

          {/* Skills */}
          <div className="mb-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-2">
              Core Technical Competencies
            </h2>
            <p className="text-xs text-slate-800 leading-relaxed font-mono">
              {masterProfile.skills.join('  •  ')}
            </p>
          </div>

          {/* Experience */}
          <div className="mb-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-3">
              Professional Experience
            </h2>

            {/* Current / Primary Role with Tailored Bullets */}
            {masterProfile.experiences.length > 0 && (
              <div className="mb-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs font-bold text-slate-900">
                    {masterProfile.experiences[0].role}
                  </span>
                  <span className="text-[11px] text-slate-500 font-medium">
                    {masterProfile.experiences[0].period} | {masterProfile.experiences[0].location}
                  </span>
                </div>
                <div className="text-xs font-semibold text-slate-700 mb-2">
                  {masterProfile.experiences[0].company}
                </div>
                <ul className="list-disc list-outside ml-4 space-y-1.5 text-xs text-slate-700">
                  {bulletsToUse.map((bullet, i) => (
                    <li key={i} className="leading-relaxed">{bullet}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Other Roles */}
            {masterProfile.experiences.slice(1).map((exp) => (
              <div key={exp.id} className="mb-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs font-bold text-slate-900">{exp.role}</span>
                  <span className="text-[11px] text-slate-500 font-medium">
                    {exp.period} | {exp.location}
                  </span>
                </div>
                <div className="text-xs font-semibold text-slate-700 mb-2">{exp.company}</div>
                <ul className="list-disc list-outside ml-4 space-y-1.5 text-xs text-slate-700">
                  {exp.bullets.map((b, idx) => (
                    <li key={idx} className="leading-relaxed">{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Education */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-2">
              Education & Qualifications
            </h2>
            {masterProfile.education.map((ed, idx) => (
              <div key={idx} className="flex justify-between items-baseline text-xs text-slate-700">
                <span className="font-semibold">{ed.degree} — {ed.institution}</span>
                <span className="text-slate-500">{ed.year}</span>
              </div>
            ))}
          </div>

        </div>

      </div>

    </div>
  );
};
